// String tables and a small ICU-style formatter: `{name}` and `{count, plural, one {# chord} other {# chords}}`.
// A page's language is its URL's (`<html lang>`); see docs/decisions.md. Also used at build time for the static pages.
import en from './en.json';
import ko from './ko.json';

export type Lang = 'en' | 'ko';
export const languages: readonly Lang[] = ['en', 'ko'];

type Messages = typeof en;
type Leaves<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${Prefix}${K}` : Leaves<T[K], `${Prefix}${K}.`>;
}[keyof T & string];
/** Every message key, e.g. `tool.editor.generate`. */
export type MessageKey = Leaves<Messages>;
export type Params = Record<string, string | number>;

const tables: Record<Lang, Messages> = { en, ko };

export function message(lang: Lang, key: MessageKey): string {
  let node: unknown = tables[lang];
  for (const part of key.split('.')) node = (node as Record<string, unknown> | undefined)?.[part];
  if (typeof node === 'string') return node;
  return lang === 'en' ? key : message('en', key);
}

/** `5`, `4.5`, `1.25`: at most two decimals, no trailing zeros (the Mac app's Format.amount). */
export function formatAmount(value: number): string {
  if (Math.abs(value - Math.round(value)) < 1e-6) return String(Math.round(value));
  return value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

/** Tempo as shown: rounded to hundredths, no trailing zeros (`120`, `92.5`). */
export function formatBPM(bpm: number): string {
  return formatAmount(Math.round(bpm * 100) / 100);
}

function value(params: Params, name: string): string {
  const raw = params[name];
  if (raw === undefined) return `{${name}}`;
  return typeof raw === 'number' ? formatAmount(raw) : raw;
}

/** Fills `{name}` and `{name, plural, one {…} other {…}}` (with `#` for the number). */
export function format(template: string, params: Params = {}, lang: Lang = 'en'): string {
  let out = '';
  let index = 0;
  while (index < template.length) {
    const open = template.indexOf('{', index);
    if (open < 0) {
      out += template.slice(index);
      break;
    }
    out += template.slice(index, open);
    // Find the matching brace (plural branches nest one level).
    let depth = 0;
    let close = open;
    for (; close < template.length; close++) {
      if (template[close] === '{') depth++;
      else if (template[close] === '}' && --depth === 0) break;
    }
    const body = template.slice(open + 1, close);
    const plural = /^\s*(\w+)\s*,\s*plural\s*,(.*)$/s.exec(body);
    if (plural) {
      const [, name, cases] = plural as unknown as [string, string, string];
      const count = Number(params[name] ?? 0);
      const branches = new Map<string, string>();
      for (const match of cases.matchAll(/(=?\w+)\s*\{([^{}]*)\}/g)) branches.set(match[1]!, match[2]!);
      const category = new Intl.PluralRules(lang).select(count);
      const branch = branches.get(`=${count}`) ?? branches.get(category) ?? branches.get('other') ?? '';
      out += branch.replace(/#/g, formatAmount(count));
    } else {
      out += value(params, body.trim());
    }
    index = close + 1;
  }
  return out;
}

export function translate(lang: Lang, key: MessageKey, params?: Params): string {
  return format(message(lang, key), params, lang);
}

/** The language of the current page (`<html lang>`); English outside a browser. */
export function pageLang(): Lang {
  const doc = (globalThis as { document?: { documentElement?: { lang?: string } } }).document;
  return doc?.documentElement?.lang === 'ko' ? 'ko' : 'en';
}

export const lang: Lang = pageLang();

/** Translates on the current page. */
export function t(key: MessageKey, params?: Params): string {
  return translate(lang, key, params);
}

// The engine reports problems in English; known messages are translated, others shown as they are.

const issuePatterns: [RegExp, MessageKey][] = [
  [/^Brackets can't be nested$/, 'tool.issues.nestedBrackets'],
  [/^Unexpected '\]'/, 'tool.issues.unexpectedBracket'],
  [/^Missing '\)'$/, 'tool.issues.missingParenthesis'],
  [/^Missing '\]'$/, 'tool.issues.missingBracket'],
  [/^Progression is too long for a MIDI file$/, 'tool.issues.progressionTooLong'],
  [/^Unknown bass note in (?<symbol>.*)$/s, 'tool.issues.unknownBass'],
  [/^Unknown chord: (?<symbol>.*)$/s, 'tool.issues.unknownChord'],
  [/^Empty brackets$/, 'tool.issues.emptyBrackets'],
  [/^Length multiplier without a chord$/, 'tool.issues.multiplierAlone'],
  [/^Nothing to repeat$/, 'tool.issues.nothingToRepeat'],
  [/^Length must be greater than 0$/, 'tool.issues.lengthNotPositive'],
  [/^Length is too long for a MIDI file$/, 'tool.issues.lengthTooLong'],
];

const warningPatterns: [RegExp, MessageKey][] = [
  [/^(?<count>\d+) note groups? couldn't be named/, 'tool.warnings.unnamed'],
  [/^Only the first (?<max>\d+) of (?<count>\d+) chords were imported\.$/, 'tool.warnings.truncatedChords'],
  [/^The file changes tempo; using the tempo at the first chord \((?<bpm>[\d.]+) BPM\)\.$/, 'tool.warnings.tempoChanges'],
  [/^The file is in (?<meter>\d+\/\d+); bars here are counted in 4\/4\.$/, 'tool.warnings.meter'],
];

function localize(text: string, patterns: [RegExp, MessageKey][], to: Lang): string {
  for (const [pattern, key] of patterns) {
    const match = pattern.exec(text);
    if (!match) continue;
    const params: Params = {};
    for (const [name, captured] of Object.entries(match.groups ?? {})) {
      params[name] = /^\d+$/.test(captured) ? Number(captured) : captured;
    }
    return translate(to, key, params);
  }
  return text;
}

export function localizeIssue(message: string, to: Lang = lang): string {
  return localize(message, issuePatterns, to);
}

export function localizeWarning(message: string, to: Lang = lang): string {
  return localize(message, warningPatterns, to);
}

const engineErrorKeys: Partial<Record<string, MessageKey>> = {
  notAMIDIFile: 'tool.engineErrors.notAMIDIFile',
  unsupportedFormat: 'tool.engineErrors.unsupportedFormat',
  unsupportedTimeDivision: 'tool.engineErrors.unsupportedTimeDivision',
  truncated: 'tool.engineErrors.truncated',
  invalidTiming: 'tool.engineErrors.invalidTiming',
  noNotes: 'tool.engineErrors.noNotes',
  noChords: 'tool.engineErrors.noChords',
};

/** A failed engine request as the page shows it. */
export function engineErrorMessage(code: string, message: string, to: Lang = lang): string {
  const key = engineErrorKeys[code];
  if (key) return translate(to, key);
  if (code === 'unusableProgression') return localizeIssue(message, to);
  if (code === 'engineCrashed') return translate(to, 'tool.errors.engineCrashed');
  if (code === 'timeout') return translate(to, 'tool.errors.engineTimeout');
  if (code === 'engineUnavailable') return translate(to, 'tool.errors.engineFailed');
  return message;
}
